import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { v20, v20Schemes } from '%evp_theme/brandbook/v20/tokens';
import {
    SCHEME_LIGHT,
    isDarkScheme,
    isHighContrastScheme,
} from '%evp_theme/brandbook/appearance/appearanceSchemes';
import createErrorTextCue from '%evp_theme/brandbook/appearance/createErrorTextCue';
import {
    HIGH_CONTRAST_FOCUS_RING_WIDTH,
    createFocusRing,
    createInsetFocusRing,
    resolveFocusRingWidth,
} from '%evp_theme/brandbook/material/service/focusRing';
import {
    STRONG_BORDER_WIDTH,
    createDisabledEdge,
    drawStrongInsetRing,
} from '%evp_theme/brandbook/material/service/strongStroke';
import createFilledButtonHover from '%evp_theme/brandbook/material/service/createFilledButtonHover';

const FONT_FAMILY = v20.fonts.body;
const HEADING_FONT_FAMILY = v20.fonts.heading;

const HIGH_CONTRAST_DISABLED_OPACITY = 0.7;
const DISABLED_ICON_BUTTON_ELEMENT = 'button&.Mui-disabled';
const CARD_BORDER_WIDTH = 1;
const RAISED_SURFACE_SHADOWS = {
    light: '0 10px 15px rgba(0, 0, 0, 0.1)',
    dark: '0 10px 15px rgba(0, 0, 0, 0.5)',
};

function createTextColoredSecondary(text, background) {
    return {
        main: text.primary,
        contrastText: background.default,
    };
}

function createTextColoredInfo(text, background) {
    return {
        main: text.primary,
        light: background.default,
        dark: text.primary,
        contrastText: background.default,
    };
}

function createHighContrastAction(palette, text, grey) {
    return {
        active: text.primary,
        hover: palette.selection.bg,
        selected: palette.selection.bgStrong,
        focus: palette.selection.bg,
        disabled: text.disabled,
        disabledBackground: grey[100],
        disabledOpacity: HIGH_CONTRAST_DISABLED_OPACITY,
    };
}

function createPalette(scheme) {
    const {
        palette,
        grey,
        text,
        background,
    } = v20Schemes[scheme];

    return {
        ...palette,
        ...(isDarkScheme(scheme) ? { mode: 'dark' } : {}),
        ...(isHighContrastScheme(scheme) ? {
            secondary: createTextColoredSecondary(text, background),
            info: createTextColoredInfo(text, background),
            action: createHighContrastAction(palette, text, grey),
        } : {}),
        grey,
        text,
        background,
    };
}

function createCustomShadows(scheme) {
    if (isHighContrastScheme(scheme)) {
        return {
            cardHover: `0 0 0 ${STRONG_BORDER_WIDTH - CARD_BORDER_WIDTH}px ${v20Schemes[scheme].text.primary}`,
        };
    }

    return {
        cardHover: isDarkScheme(scheme) ? RAISED_SURFACE_SHADOWS.dark : RAISED_SURFACE_SHADOWS.light,
    };
}

const breakpoints = {
    values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
    },
};

const typography = {
    fontFamily: FONT_FAMILY,
    displayS: {
        fontFamily: HEADING_FONT_FAMILY,
        ...v20.typeScale.displayS,
    },
    headlineL: {
        fontFamily: HEADING_FONT_FAMILY,
        ...v20.typeScale.headlineL,
    },
    headlineM: {
        fontFamily: HEADING_FONT_FAMILY,
        ...v20.typeScale.headlineM,
    },
    headlineS: {
        fontFamily: HEADING_FONT_FAMILY,
        ...v20.typeScale.headlineS,
    },
    titleL: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.titleL,
    },
    titleM: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.titleM,
    },
    titleS: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.titleS,
    },
    bodyM: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.bodyM,
    },
    bodyS: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.bodyS,
    },
    labelL: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.labelL,
    },
    labelM: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.labelM,
    },
    labelS: {
        fontFamily: FONT_FAMILY,
        ...v20.typeScale.labelS,
    },
};

const shape = {
    borderRadius: v20.radii.default,
    borderRadiusSmall: v20.radii.small,
    borderRadiusLarge: v20.radii.large,
};

const typographyVariantMapping = {
    displayS: 'h1',
    headlineL: 'h2',
    headlineM: 'h3',
    headlineS: 'h4',
    titleL: 'h5',
    titleM: 'h6',
    titleS: 'h6',
    bodyM: 'p',
    bodyS: 'p',
    labelL: 'span',
    labelM: 'span',
    labelS: 'span',
};

const BASE_ROOT_FONT_SIZE_PX = 16;

function scaleRemValue(remValue, factor) {
    return `${Number((parseFloat(remValue) * factor).toFixed(5))}rem`;
}

function scaleFontSizesDeep(node, factor) {
    if (node === null || typeof node !== 'object') {
        return node;
    }

    const scaled = {};
    Object.keys(node).forEach((key) => {
        const value = node[key];
        scaled[key] = key === 'fontSize' && typeof value === 'string' && value.endsWith('rem')
            ? scaleRemValue(value, factor)
            : scaleFontSizesDeep(value, factor);
    });

    return scaled;
}

const DISABLED_EDGE = createDisabledEdge(STRONG_BORDER_WIDTH);
const DISABLED_BUTTON_EDGE = {
    '&.Mui-disabled': DISABLED_EDGE,
};

const baseComponents = {
    MuiTypography: {
        defaultProps: {
            variantMapping: typographyVariantMapping,
        },
    },
    MuiCard: {
        styleOverrides: {
            root: ({ theme: t }) => ({
                borderRadius: t.shape.borderRadiusLarge,
                boxShadow: 'none',
                border: `${CARD_BORDER_WIDTH}px solid ${t.palette.divider}`,
            }),
        },
    },
};

function ringVanishesOnBrandSurface(palette) {
    return palette.primary.main === palette.brandSurface.main;
}

function createHighContrastFocusRing(t) {
    const ring = createFocusRing(t.palette.primary.main, HIGH_CONTRAST_FOCUS_RING_WIDTH);

    return ringVanishesOnBrandSurface(t.palette)
        ? { ...ring, boxShadow: `0 0 0 ${ring.outlineOffset}px ${t.palette.brandSurface.focus}` }
        : ring;
}

function createHighContrastInsetFocusRing(t) {
    return {
        ...createInsetFocusRing(t.palette.primary.main, HIGH_CONTRAST_FOCUS_RING_WIDTH),
        boxShadow: 'none',
    };
}

function resolveHighContrastButtonTone(ownerState, t) {
    const paletteColor = t.palette[ownerState.color];

    if (paletteColor?.contrastText) {
        return paletteColor;
    }

    return {
        main: t.palette.text.primary,
        contrastText: t.palette.background.paper,
    };
}

function createHighContrastButtonHover(ownerState, t) {
    const tone = resolveHighContrastButtonTone(ownerState, t);

    if (ownerState.variant === 'contained') {
        return {
            '&:hover': createFilledButtonHover(t, tone, tone.contrastText),
        };
    }

    return {
        '&:hover': {
            backgroundColor: tone.main,
            color: tone.contrastText,
            borderColor: tone.main,
            '@media (hover: none)': {
                backgroundColor: 'transparent',
                color: ownerState.color === 'inherit' ? 'inherit' : tone.main,
            },
        },
    };
}

function createHighContrastStandardAlert(status) {
    return ({ theme: t }) => ({
        color: t.palette.text.primary,
        backgroundColor: t.palette[status].light,
        border: `${STRONG_BORDER_WIDTH}px solid ${t.palette[status].dark}`,
        '& .MuiAlert-icon': {
            color: t.palette[status].dark,
        },
    });
}

function createHighContrastFilledAlert({ ownerState, theme: t }) {
    const tone = t.palette[ownerState.color || ownerState.severity];

    return tone?.contrastText ? { color: tone.contrastText } : {};
}

function createSurfaceAutofill(t) {
    return {
        '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${t.palette.background.paper} inset`,
            WebkitTextFillColor: t.palette.text.primary,
            caretColor: t.palette.text.primary,
        },
    };
}

const highContrastComponents = {
    MuiCssBaseline: {
        styleOverrides: t => ({
            '::selection': {
                backgroundColor: t.palette.primary.main,
                color: t.palette.primary.contrastText,
            },
            '*:focus-visible': createHighContrastFocusRing(t),
            '.MuiInputBase-input:focus-visible': {
                boxShadow: 'none',
            },
            'a:not([class])': {
                textDecoration: 'underline',
            },
        }),
    },
    MuiButtonBase: {
        styleOverrides: {
            root: ({ theme: t }) => ({
                '&.Mui-focusVisible': createHighContrastFocusRing(t),
            }),
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: ({ theme: t }) => ({
                [DISABLED_ICON_BUTTON_ELEMENT]: DISABLED_EDGE,
                '&:hover': {
                    backgroundColor: t.palette.action.hover,
                    boxShadow: drawStrongInsetRing(t, 'currentColor'),
                    '@media (hover: none)': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                },
            }),
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: ({ theme: t }) => ({
                '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
                    backgroundColor: t.palette.action.selected,
                },
                '&.Mui-focusVisible': createHighContrastInsetFocusRing(t),
            }),
        },
    },
    MuiTab: {
        styleOverrides: {
            root: ({ theme: t }) => ({
                '&.Mui-focusVisible': createHighContrastInsetFocusRing(t),
            }),
        },
    },
    MuiChip: {
        styleOverrides: {
            root: ({ ownerState, theme: t }) => (ownerState.color === 'default'
                ? {
                    backgroundColor: 'transparent',
                    border: `${STRONG_BORDER_WIDTH}px solid ${t.palette.text.primary}`,
                    '& .MuiChip-deleteIcon': {
                        color: t.palette.text.primary,
                        '&:hover': {
                            color: t.palette.primary.main,
                        },
                    },
                }
                : {}),
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: ({ theme: t }) => ({
                backgroundColor: t.palette.text.primary,
                color: t.palette.background.default,
            }),
            arrow: ({ theme: t }) => ({
                color: t.palette.text.primary,
            }),
        },
    },
    MuiAlert: {
        styleOverrides: {
            standardError: createHighContrastStandardAlert('error'),
            standardWarning: createHighContrastStandardAlert('warning'),
            standardSuccess: createHighContrastStandardAlert('success'),
            standardInfo: createHighContrastStandardAlert('info'),
            filled: createHighContrastFilledAlert,
        },
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: {
                '&.Mui-error': createErrorTextCue(),
            },
        },
    },
    MuiLink: {
        defaultProps: {
            underline: 'always',
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: ({ ownerState, theme: t }) => (ownerState.variant === 'elevation' && ownerState.elevation > 0
                ? { backgroundImage: 'none', border: `1px solid ${t.palette.divider}` }
                : {}),
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: ({ theme: t }) => ({
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: HIGH_CONTRAST_FOCUS_RING_WIDTH,
                    borderColor: t.palette.primary.main,
                },
            }),
            notchedOutline: ({ theme: t }) => ({
                borderWidth: STRONG_BORDER_WIDTH,
                borderColor: t.palette.text.secondary,
            }),
            input: ({ theme: t }) => createSurfaceAutofill(t),
        },
    },
    MuiButton: {
        styleOverrides: {
            root: ({ ownerState, theme: t }) => ({
                ...createHighContrastButtonHover(ownerState, t),
                '&.Mui-focusVisible': createHighContrastFocusRing(t),
            }),
            text: DISABLED_BUTTON_EDGE,
            contained: DISABLED_BUTTON_EDGE,
            outlined: ({ theme: t }) => ({
                borderWidth: STRONG_BORDER_WIDTH,
                '&:hover': {
                    borderWidth: STRONG_BORDER_WIDTH,
                },
                '&.Mui-disabled': {
                    borderWidth: STRONG_BORDER_WIDTH,
                    borderColor: t.palette.action.disabled,
                },
            }),
        },
    },
};

const darkComponents = {
    MuiOutlinedInput: {
        styleOverrides: {
            input: ({ theme: t }) => createSurfaceAutofill(t),
        },
    },
};

function createComponents(scheme) {
    return {
        ...baseComponents,
        ...(isDarkScheme(scheme) ? darkComponents : {}),
        ...(isHighContrastScheme(scheme) ? highContrastComponents : {}),
    };
}

function buildRedesignTheme(scheme) {
    return responsiveFontSizes(
        createTheme({
            palette: createPalette(scheme),
            typography,
            breakpoints,
            shape: {
                ...shape,
                focusRingWidth: resolveFocusRingWidth(scheme),
                strongBorderWidth: STRONG_BORDER_WIDTH,
            },
            spacing: v20.spacing,
            customShadows: createCustomShadows(scheme),
            components: createComponents(scheme),
            appearance: {
                highContrast: isHighContrastScheme(scheme),
            },
        }),
        {
            breakpoints: ['sm', 'md', 'lg'],
            variants: [
                'displayS',
                'headlineL',
                'headlineM',
                'headlineS',
                'titleL',
                'titleM',
            ],
        },
    );
}

const themesByScheme = new Map();
const chromeThemesByKey = new Map();

export function createRedesignTheme(scheme = SCHEME_LIGHT) {
    if (!themesByScheme.has(scheme)) {
        themesByScheme.set(scheme, buildRedesignTheme(scheme));
    }

    return themesByScheme.get(scheme);
}

const theme = createRedesignTheme(SCHEME_LIGHT);

export default theme;

function buildChromeTheme(schemeTheme, factor, hostRootFontSizePx) {
    return createTheme(schemeTheme, {
        typography: {
            ...scaleFontSizesDeep(schemeTheme.typography, factor),
            htmlFontSize: hostRootFontSizePx,
            pxToRem: size => scaleRemValue(schemeTheme.typography.pxToRem(size), factor),
        },
    });
}

export function createChromeTheme(hostRootFontSizePx, scheme = SCHEME_LIGHT) {
    const schemeTheme = createRedesignTheme(scheme);
    const factor = BASE_ROOT_FONT_SIZE_PX / hostRootFontSizePx;
    if (factor === 1) {
        return schemeTheme;
    }

    const key = `${scheme}:${hostRootFontSizePx}`;
    if (!chromeThemesByKey.has(key)) {
        chromeThemesByKey.set(key, buildChromeTheme(schemeTheme, factor, hostRootFontSizePx));
    }

    return chromeThemesByKey.get(key);
}
